/**
 * Parser best-effort de SELECT para importar en QueryBuilder.
 * Solo permite SELECT; extrae FROM, SELECT list, JOINs, WHERE, GROUP BY, LIMIT, OFFSET.
 */

const FORBIDDEN =
  /\b(INSERT|UPDATE|DELETE|DROP|CREATE|TRUNCATE|ALTER|EXECUTE|EXEC|;\s*SELECT)\b/i;

export interface ParsedSelectConfig {
  baseTable: string;
  selectedFields: string[];
  joins: Array< { table: string; on: { localField: string; foreignField: string } }>;
  filters: Array<{ field: string; operator: string; value: string }>;
  groupByFields: string[];
  aggregation: Array<{ field: string; agreggation: string; innerSql?: string }>;
  limit: number;
  offset: number;
  /** Texto crudo de ORDER BY, por ejemplo: `SUM(t.volumen) DESC` */
  orderByRaw?: string;
  aggregateAliases: Record<string, string>;
}

function stripComments(s: string): string {
  return s
    .replace(/--[^\n]*/g, "\n")
    .replace(/\/\*[\s\S]*?\*\//g, "\n");
}

/** Comprueba que la query sea solo SELECT (sin INSERT/UPDATE/DELETE/...) */
export function isAllowedSelectOnly(sql: string): { allowed: boolean; message?: string } {
  const trimmed = sql.trim().toUpperCase();
  if (!trimmed.startsWith("SELECT")) {
    return { allowed: false, message: "La consulta debe comenzar con SELECT." };
  }
  if (FORBIDDEN.test(sql)) {
    return { allowed: false, message: "Solo se permiten consultas SELECT (no INSERT, UPDATE, DELETE, etc.)." };
  }
  return { allowed: true };
}

/**
 * Parsea un SELECT y devuelve la configuración para el QueryBuilder.
 * Es best-effort: SQL muy compleja (subconsultas, etc.) puede no parsearse bien.
 */
export function parseSelectSql(sql: string): { ok: true; config: ParsedSelectConfig } | { ok: false; message: string } {
  const check = isAllowedSelectOnly(sql);
  if (!check.allowed) return { ok: false, message: check.message! };

  const cleaned = stripComments(sql).replace(/\s+/g, " ").trim();
  const upper = cleaned.toUpperCase();

  let baseTable = "";
  const joins: ParsedSelectConfig["joins"] = [];
  let selectList = "";
  let whereClause = "";
  let groupByClause = "";
  let limitNum = 5000;
  let offsetNum = 0;

  // FROM ... (hasta el primer JOIN o WHERE o GROUP BY o LIMIT)
  const fromIdx = upper.indexOf(" FROM ");
  if (fromIdx === -1) return { ok: false, message: "No se encontró FROM en la consulta." };

  const selectPart = cleaned.slice(0, fromIdx).replace(/^select\s+/i, "").trim();
  selectList = selectPart;

  const afterFrom = cleaned.slice(fromIdx + 6).trim();
  const fromMatch = afterFrom.match(/^([a-z_][a-z0-9_.]*)\s+/i);
  if (!fromMatch) return { ok: false, message: "No se pudo extraer la tabla del FROM." };
  baseTable = fromMatch[1].trim();
  let rest = afterFrom.slice(fromMatch[0].length).trim();

  // WHERE / GROUP BY / LIMIT / OFFSET (posiciones)
  const whereIdx = rest.toUpperCase().indexOf(" WHERE ");
  const gbIdx = rest.toUpperCase().indexOf(" GROUP BY ");
  const orderIdx = rest.toUpperCase().indexOf(" ORDER BY ");
  const limIdx = rest.toUpperCase().indexOf(" LIMIT ");
  const offIdx = rest.toUpperCase().indexOf(" OFFSET ");
  const endJoin = [whereIdx, gbIdx, orderIdx, limIdx, offIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const joinSection = endJoin !== undefined ? rest.slice(0, endJoin).trim() : rest;

  // JOINs en el bloque entre FROM y WHERE/GROUP BY/LIMIT
  const joinRegex = /(?:LEFT|RIGHT|INNER)?\s*JOIN\s+([a-z_][a-z0-9_.]*)\s+ON\s+([^\s=]+)\s*=\s*([^\s]+?)(?=\s*(?:LEFT|RIGHT|INNER)?\s*JOIN\s+|$)/gi;
  let j;
  while ((j = joinRegex.exec(joinSection)) !== null) {
    joins.push({
      table: j[1].trim(),
      on: {
        localField: j[2].trim(),
        foreignField: j[3].trim().replace(/,\s*$/, ""),
      },
    });
  }

  if (whereIdx !== -1) {
    const afterWhere = rest.slice(whereIdx + 7);
    const next = [gbIdx, orderIdx, limIdx, offIdx].filter((i) => i > whereIdx);
    const end = next.length ? Math.min(...next.map((i) => i - whereIdx - 7)) : afterWhere.length;
    whereClause = afterWhere.slice(0, end).trim();
  }
  if (gbIdx !== -1) {
    const afterGb = rest.slice(gbIdx + 9);
    const next = [orderIdx, limIdx, offIdx].filter((i) => i > gbIdx);
    const end = next.length ? Math.min(...next.map((i) => i - gbIdx - 9)) : afterGb.length;
    groupByClause = afterGb.slice(0, end).replace(/\s+/g, " ").trim();
  }
  if (limIdx !== -1) {
    const afterLim = rest.slice(limIdx + 6).trim();
    const numMatch = afterLim.match(/^(\d+)/);
    if (numMatch) limitNum = parseInt(numMatch[1], 10) || 5000;
  }
  if (offIdx !== -1) {
    const afterOff = rest.slice(offIdx + 7).trim();
    const numMatch = afterOff.match(/^(\d+)/);
    if (numMatch) offsetNum = parseInt(numMatch[1], 10) || 0;
  }

  let orderByRaw = "";
  if (orderIdx !== -1) {
    const ORDER_BY_LEN = " ORDER BY ".length;
    const afterOrder = rest.slice(orderIdx + ORDER_BY_LEN).trim();
    const next = [limIdx, offIdx].filter((i) => i > orderIdx);
    const end = next.length
      ? Math.min(...next.map((i) => i - orderIdx - ORDER_BY_LEN))
      : afterOrder.length;
    orderByRaw = afterOrder.slice(0, end).replace(/;$/, "").trim();
  }

  // Parsear SELECT list: "col1, col2, SUM(t.x) AS alias, t.y"
  const selectedFields: string[] = [];
  const aggregation: ParsedSelectConfig["aggregation"] = [];
  const aggregateAliases: Record<string, string> = {};
  let complexAggCounter = 0;
  const selectItems = splitTopLevel(selectList, ",");
  for (const item of selectItems) {
    const t = item.trim();
    const { exprRaw, alias } = parseAliasFromSelectItem(t);
    const expr = exprRaw.replace(/^["']|["']$/g, "");
    const agg = matchLeadingAggregate(expr);
    if (agg) {
      const mapped = mapAggregateInnerToField(agg.inner, baseTable, complexAggCounter);
      complexAggCounter = mapped.nextComplexIndex;
      const field = mapped.field;
      if (!field || field === "*") {
        continue;
      }
      selectedFields.push(field);
      const aggItem: { field: string; agreggation: string; innerSql?: string } = {
        field,
        agreggation: agg.func,
      };
      if (mapped.innerSql) aggItem.innerSql = mapped.innerSql;
      aggregation.push(aggItem);
      if (alias) aggregateAliases[field] = alias;
    } else {
      const field = expr.trim();
      if (field && field !== "*") {
        selectedFields.push(field);
        if (alias) aggregateAliases[field] = alias;
      }
    }
  }

  // Filtros desde WHERE (simplificado: solo field op 'value' y AND)
  const filters: ParsedSelectConfig["filters"] = [];
  if (whereClause) {
    const andParts = whereClause.split(/\s+AND\s+/i);
    for (const part of andParts) {
      const m = part.trim().match(/^([a-z0-9_.]+)\s*(=|>|<|>=|<=|ILIKE|LIKE)\s*['"]?([^'"]*)['"]?$/i);
      if (m) {
        filters.push({
          field: m[1].trim(),
          operator: m[2].toUpperCase() === "LIKE" ? "ILIKE" : (m[2] as any),
          value: m[3].trim(),
        });
      }
    }
  }

  // No usar split(",") simple: rompe CONCAT(a, b, c) y otras funciones con comas en GROUP BY.
  const groupByFields = groupByClause
    ? splitTopLevel(groupByClause, ",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return {
    ok: true,
    config: {
      baseTable,
      selectedFields,
      joins,
      filters,
      groupByFields,
      aggregation,
      limit: limitNum,
      offset: offsetNum,
      orderByRaw: orderByRaw || undefined,
      aggregateAliases,
    },
  };
}

/** Split por comas solo al nivel superior (no dentro de paréntesis). */
function splitTopLevel(str: string, sep: string): string[] {
  const out: string[] = [];
  let level = 0;
  let start = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === "(") level++;
    else if (str[i] === ")") level--;
    else if (level === 0 && str.slice(i, i + sep.length) === sep) {
      out.push(str.slice(start, i));
      start = i + sep.length;
    }
  }
  out.push(str.slice(start));
  return out;
}

/** Quita casts PostgreSQL tipo ::numeric del final de un identificador. */
function stripCast(s: string): string {
  return s.replace(/::\w+(?:\[\])?/gi, "").trim();
}

/** municipios.nombre → cualificado; carbono_raiz + base existencias_forestales → existencias_forestales.carbono_raiz */
function qualifyBareField(ident: string, baseTable: string): string {
  const t = stripCast(ident.trim());
  if (!t || t === "*") return t;
  if (t.includes(".")) return t;
  return `${baseTable}.${t}`;
}

/**
 * SUM/AVG/... con paréntesis anidados (p. ej. SUM(COALESCE(carbono_raiz, 0))).
 */
function matchLeadingAggregate(expr: string): { func: string; inner: string } | null {
  const m = expr.match(/^\s*(SUM|AVG|COUNT|MIN|MAX)\s*\(/i);
  if (!m) return null;
  const func = m[1].toUpperCase();
  let i = (m.index ?? 0) + m[0].length;
  let depth = 1;
  const start = i;
  while (i < expr.length && depth > 0) {
    if (expr[i] === "(") depth++;
    else if (expr[i] === ")") depth--;
    i++;
  }
  if (depth !== 0) return null;
  const inner = expr.slice(start, i - 1);
  const rest = expr.slice(i).trim();
  if (rest.length > 0) return null;
  return { func, inner };
}

/** Primer argumento de COALESCE( x , … ) o null. */
function firstArgOfCoalesce(inner: string): string | null {
  const t = inner.trim();
  const open = t.match(/^COALESCE\s*\(\s*/i);
  if (!open) return null;
  let i = open[0].length;
  let depth = 0;
  const start = i;
  for (; i < t.length; i++) {
    const ch = t[i];
    if (ch === "(") depth++;
    else if (ch === ")") {
      if (depth === 0) break;
      depth--;
    } else if (ch === "," && depth === 0) {
      return t.slice(start, i).trim();
    }
  }
  return null;
}

/** Expresión interna de agregado → clave de campo para el QueryBuilder (tabla.columna). */
function simpleAggregateInnerToFieldKey(inner: string, baseTable: string): string {
  const trimmed = inner.trim();
  const coalesceFirst = firstArgOfCoalesce(trimmed);
  const raw = stripCast((coalesceFirst ?? trimmed).trim());
  return qualifyBareField(raw, baseTable);
}

/**
 * Agregados con una sola columna o un solo COALESCE(...) se mapean a tabla.campo.
 * Expresiones (p. ej. COALESCE(a,0)+COALESCE(b,0)) usan clave sintética + innerSql.
 */
function isSimpleAggregateInner(inner: string): boolean {
  const t = inner.trim();
  if (!t) return false;
  const ident = stripCast(t);
  if (/^[a-z_][a-z0-9_.]*$/i.test(ident)) return true;
  if (/^COALESCE\s*\(/i.test(t) && t.endsWith(")")) {
    let depth = 0;
    for (let i = 0; i < t.length; i++) {
      if (t[i] === "(") depth++;
      else if (t[i] === ")") depth--;
      else if (depth === 0 && t[i] === "+") return false;
    }
    return true;
  }
  return false;
}

function mapAggregateInnerToField(
  inner: string,
  baseTable: string,
  complexIndex: number
): { field: string; innerSql?: string; nextComplexIndex: number } {
  if (isSimpleAggregateInner(inner)) {
    const field = simpleAggregateInnerToFieldKey(inner, baseTable);
    return { field, nextComplexIndex: complexIndex };
  }
  const field = `__agg_expr_${complexIndex}`;
  return {
    field,
    innerSql: inner.trim(),
    nextComplexIndex: complexIndex + 1,
  };
}

function parseAliasFromSelectItem(t: string): { exprRaw: string; alias: string | null } {
  const asQuoted = t.match(/\s+AS\s+(["'])([^"']*)\1\s*$/i);
  if (asQuoted) {
    return {
      exprRaw: t.slice(0, t.length - asQuoted[0].length).trim(),
      alias: asQuoted[2].trim(),
    };
  }
  const asBare = t.match(/\s+AS\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*$/i);
  if (asBare) {
    return {
      exprRaw: t.slice(0, t.length - asBare[0].length).trim(),
      alias: asBare[1].trim(),
    };
  }
  return { exprRaw: t.trim(), alias: null };
}
